import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import { useState } from "react";
import { GREEN, YELLOW, RED, BLUE } from "@helper/colors";
import { useTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import { Formik, Form } from "formik";
import { updateTreeSoil } from "@modules/Trees/services/treeCrud";
import { useMutation, useQueryClient } from "react-query";

/* =========================================================
   Types
========================================================= */

type Soil = {
  max_f: number;
  max_nf: number;
  pwp: number;
  yellow_threshold_swt?: number;
  green_threshold_swt?: number;
  blue_threshold_swt?: number;
};

type Tree = {
  id: number;
  soil?: Soil;
  installations: any[];
};

type Area = {
  tree_set?: Tree[];
};

type Project = {
  areas?: Area[];
};

type PaginatedTrees = {
  results: Tree[];
};

type UpdateTreeSoilPayload = {
  id: number;
  formData: {
    soil: Soil;
  };
};

const isPaginatedTrees = (data: any): data is PaginatedTrees => data && Array.isArray(data.results);

/* =========================================================
   Helpers
========================================================= */

const marks = (thresholds: number[], type: string) => [
  {
    value: type === "soilMoisture" ? thresholds[0] : thresholds[2],
    label: type === "soilMoisture" ? `${thresholds[0]}%` : `${thresholds[2]}pF`,
    name: "yellow",
  },
  {
    value: thresholds[1],
    label: type === "soilMoisture" ? `${thresholds[1]}%` : `${thresholds[1]}pF`,
    name: "green",
  },
  {
    value: type === "soilMoisture" ? thresholds[2] : thresholds[0],
    label: type === "soilMoisture" ? `${thresholds[2]}%` : `${thresholds[0]}pF`,
    name: "blue",
  },
];

function valuetext(value: number) {
  return `${value}%`;
}

/* =========================================================
   Moisture Box
========================================================= */

function MoistureBox({
  t,
  thresholds,
  setThresholds,
  setFieldValue,
  initialMarks,
  type,
  isLoading,
}: any) {
  const maxValue = type === "soilMoisture" ? 100 : 4.2;
  const getPercentage = (value: number) => (value / maxValue) * 100;

  return (
    <>
      <div className="soil-threshold-title">
        {type === "soilMoisture" ? t("soil_moisture_edit") : t("soil_water_tension_edit")}
      </div>

      <Box sx={{ width: 400 }}>
        <Box sx={{ position: "relative", height: "20px", mb: 2 }}>
          <Box
            sx={{
              position: "absolute",
              left: 0,
              width: `${getPercentage(thresholds[0])}%`,
              height: "100%",
              backgroundColor: type === "soilMoisture" ? RED : BLUE,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              left: `${getPercentage(thresholds[0])}%`,
              width: `${getPercentage(thresholds[1] - thresholds[0])}%`,
              height: "100%",
              backgroundColor: type === "soilMoisture" ? YELLOW : GREEN,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              left: `${getPercentage(thresholds[1])}%`,
              width: `${getPercentage(thresholds[2] - thresholds[1])}%`,
              height: "100%",
              backgroundColor: type === "soilMoisture" ? GREEN : YELLOW,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              left: `${getPercentage(thresholds[2])}%`,
              width: `${100 - getPercentage(thresholds[2])}%`,
              height: "100%",
              backgroundColor: type === "soilMoisture" ? BLUE : RED,
            }}
          />

          <Slider
            aria-labelledby="track-false-range-slider"
            getAriaValueText={valuetext}
            defaultValue={thresholds}
            value={thresholds}
            min={0}
            max={type === "soilMoisture" ? 100 : 4.2}
            step={type === "soilMoisture" ? 1 : 0.1}
            marks={initialMarks}
            onChange={(_, newValue) => {
              if (!Array.isArray(newValue)) return;

              const unique = new Set(newValue);
              if (unique.size !== newValue.length) return;

              setThresholds(newValue);

              if (type === "soilMoisture") {
                setFieldValue("pwp", newValue[0]);
                setFieldValue("max_nf", newValue[1]);
                setFieldValue("max_f", newValue[2]);
              } else {
                setFieldValue("blue_threshold_swt", newValue[0]);
                setFieldValue("green_threshold_swt", newValue[1]);
                setFieldValue("yellow_threshold_swt", newValue[2]);
              }
            }}
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 7 }}>
          <Button
            onClick={() => {
              if (type === "soilMoisture") {
                setThresholds([10, 25, 45]);
                setFieldValue("pwp", 10);
                setFieldValue("max_nf", 25);
                setFieldValue("max_f", 45);
              } else {
                setThresholds([1.8, 2.7, 3.0]);
                setFieldValue("blue_threshold_swt", 1.8);
                setFieldValue("green_threshold_swt", 2.7);
                setFieldValue("yellow_threshold_swt", 3.0);
              }
            }}
          >
            {t("set_default")}
          </Button>

          <Button type="submit">{isLoading ? t("button.updating") : t("button.update")}</Button>
        </Box>
      </Box>
    </>
  );
}

/* =========================================================
   Main Component
========================================================= */

function SoilThresholdSlider({ tree, setEdit }: { tree: Tree; setEdit: any }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [thresholds, setThresholds] = useState<number[]>([
    tree.soil?.pwp ?? 10,
    tree.soil?.max_nf ?? 25,
    tree.soil?.max_f ?? 45,
  ]);

  const [thresholdsWM, setThresholdsWM] = useState<number[]>([
    tree.soil?.blue_threshold_swt ?? 1.8,
    tree.soil?.green_threshold_swt ?? 2.7,
    tree.soil?.yellow_threshold_swt ?? 3.0,
  ]);

  const { isLoading, mutateAsync } = useMutation<any, any, UpdateTreeSoilPayload>(
    "updateTreeSoil",
    updateTreeSoil,
    {
      onSuccess: () => {
        /* -------- project cache -------- */
        queryClient
          .getQueriesData({ predicate: (q) => q.queryKey[0] === "project" })
          .forEach(([key]) => {
            queryClient.setQueryData<Project>(key, (old) => {
              if (!old) return old;
              return {
                ...old,
                areas: old.areas?.map((a) => ({
                  ...a,
                  tree_set: a.tree_set?.map((t) =>
                    t.id === tree.id
                      ? {
                          ...t,
                          soil: {
                            ...t.soil,
                            max_f: thresholds[2],
                            max_nf: thresholds[1],
                            pwp: thresholds[0],
                            yellow_threshold_swt: thresholdsWM[2],
                            green_threshold_swt: thresholdsWM[1],
                            blue_threshold_swt: thresholdsWM[0],
                          },
                        }
                      : t
                  ),
                })),
              };
            });
          });

        /* -------- trees cache -------- */
        queryClient
          .getQueriesData({ predicate: (q) => q.queryKey[0] === "trees" })
          .forEach(([key]) => {
            queryClient.setQueryData<Tree[] | PaginatedTrees>(key, (old) => {
              if (!old) return old;

              if (isPaginatedTrees(old)) {
                return {
                  ...old,
                  results: old.results.map((t) =>
                    t.id === tree.id
                      ? {
                          ...t,
                          soil: {
                            ...t.soil,
                            max_f: thresholds[2],
                            max_nf: thresholds[1],
                            pwp: thresholds[0],
                            yellow_threshold_swt: thresholdsWM[2],
                            green_threshold_swt: thresholdsWM[1],
                            blue_threshold_swt: thresholdsWM[0],
                          },
                        }
                      : t
                  ),
                };
              }

              return old.map((t) =>
                t.id === tree.id
                  ? {
                      ...t,
                      soil: {
                        ...t.soil,
                        max_f: thresholds[2],
                        max_nf: thresholds[1],
                        pwp: thresholds[0],
                        yellow_threshold_swt: thresholdsWM[2],
                        green_threshold_swt: thresholdsWM[1],
                        blue_threshold_swt: thresholdsWM[0],
                      },
                    }
                  : t
              );
            });
          });

        queryClient.invalidateQueries(["tree", tree.id]);
        setEdit(false);
      },
    }
  );

  const handleSubmit = async (values: any) => {
    await mutateAsync({
      id: tree.id,
      formData: { soil: values },
    });
  };

  return (
    <Formik
      initialValues={{
        pwp: thresholds[0],
        max_nf: thresholds[1],
        max_f: thresholds[2],
        yellow_threshold_swt: thresholdsWM[2],
        green_threshold_swt: thresholdsWM[1],
        blue_threshold_swt: thresholdsWM[0],
      }}
      onSubmit={handleSubmit}
    >
      {({ setFieldValue }) => (
        <Form>
          <MoistureBox
            t={t}
            thresholds={thresholds}
            setThresholds={setThresholds}
            setFieldValue={setFieldValue}
            initialMarks={marks(thresholds, "soilMoisture")}
            type="soilMoisture"
            isLoading={isLoading}
          />

          <MoistureBox
            t={t}
            thresholds={thresholdsWM}
            setThresholds={setThresholdsWM}
            setFieldValue={setFieldValue}
            initialMarks={marks(thresholdsWM, "soilWaterTension")}
            type="soilWaterTension"
            isLoading={isLoading}
          />
        </Form>
      )}
    </Formik>
  );
}

export default SoilThresholdSlider;
